import { useState, useEffect } from "react";
import Axios from "axios";
import MultiStepLayout from "./components/MultiStepLayout";
import Result from "./components/Result";
import jstz from 'jstz';
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import 'react-phone-input-2/lib/style.css';
import './globals.css';
import { useTranslation } from "react-i18next";
import { isPossiblePhoneNumber } from 'react-phone-number-input'

function App({ leadFormId, language }) {
  const id = leadFormId;
  const timezone = jstz.determine();

  // Regex pattern for first and last name validation
  // eslint-disable-next-line no-useless-escape
  const nameRegex = /^(?!.*[()])[A-Za-z\s'\-\.^]+$/;

  const { i18n, t } = useTranslation();

  const [step, setStep] = useState(1);
  const [leadForm, setLeadForm] = useState();
  const [loaded, setLoaded] = useState(false);
  const [lead, setLead] = useState({ errors: {} });
  const [submitting, setSubmitting] = useState(false);
  const [geoLocation, setGeoLocation] = useState({ city: '', state: '', countryName: '', countryCode: '', phoneCode: 'US' });
  const [step2Valid, setStep2Valid] = useState(false);
  const [useMetricSystem, setUseMetricSystem] = useState(true);
  const [isMobile, setIsMobile] = useState(true);
  const [activeElement, setActiveElement] = useState(null);

  const LOCALE_TO_LANGUAGE_NAME = {
    en: 'English', ar: 'Arabic', fr: 'French', de: 'German', he: 'Hebrew',
    it: 'Italian', ja: 'Japanese', pt: 'Portuguese', ru: 'Russian', es: 'Spanish',
    tl: 'Tagalog', zh: 'Mandarin', yue: 'Cantonese'
  };

  const fetchGeoLocationData = async () => {
    try {
      const res = await Axios.get('https://geolocation-db.com/json/')
      setGeoLocation({ city: res.data.city, state: res.data.state, countryName: res.data.country_name, phoneCode: res.data.country_code, countryCode: res.data.country_code.toLowerCase() });

      if (['us'].includes(res.data.country_code.toLowerCase())) {
        setUseMetricSystem(false);
      }

      if (['us', 'ca'].includes(res.data.country_code.toLowerCase())) {
        setLead({ ...lead, language: "English" });
      }
    } catch (e) {
      // console.log(e);
    }
  }

  useEffect(() => {
    i18n.changeLanguage(language || 'en');

    setFormWidth();
    fetchGeoLocationData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener("resize", () => {
      setFormWidth();
    });
    return () => {
      window.removeEventListener("resize", () => {
        setFormWidth();
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormWidth = () => {
    const containerWidth = document.querySelector(`[data-pulse-form-id="${leadFormId}"]`)?.offsetWidth;

    if (containerWidth) {
      containerWidth < 750 ? setIsMobile(true) : setIsMobile(false);
    }
  }

  const onChange = (name, value) => {
    const updates = { [name]: value };
    const errorUpdates = { ...lead.errors };
    if (name === 'messageChannel') {
      updates.messageChannelConsent = false;
      delete errorUpdates.messageChannelConsent;
    }
    if (name === 'messageChannelConsent') {
      if (value) {
        delete errorUpdates.messageChannelConsent;
      } else {
        errorUpdates.messageChannelConsent = t('lead.errors.messageChannelConsent');
      }
    }
    if (errorUpdates[name] && name !== 'messageChannelConsent') {
      delete errorUpdates[name];
    }
    setLead({...lead, ...updates, errors: errorUpdates});
    if (step === 2) validateStep2({...lead, ...updates});
  }

  const onBlur = (name, value) => {
    if (step === 1) {
      validateStep1Fields(name, value);
    }
    if (step === 2) {
      validateStep2Fields(name, value);
      validateStep2({ ...lead, [name]: value });
    }
  }

  const validateStep1Fields = (name, value) => {
    if (name === 'fullName') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, fullName: t('lead.errors.required')}});
      } else if (value.split(' ').length < 2) {
        setLead({...lead, errors: {...lead.errors, fullName: t('lead.errors.lastName')}});
      } else if (!nameRegex.test(value)) {
      setLead({
        ...lead,
        errors: {
          ...lead.errors,
          fullName:
            t('lead.errors.nameValidation'),
        },
      });
    } else {
        // Validate first_name and last_name length (1-50 characters each)
        const nameParts = value.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(' ');
        
        if (firstName.length < 1 || firstName.length > 50) {
          setLead({
            ...lead,
            errors: {
              ...lead.errors,
              fullName: t('lead.errors.nameLength'),
            },
          });
        } else if (lastName.length < 1 || lastName.length > 50) {
          setLead({
            ...lead,
            errors: {
              ...lead.errors,
              fullName: t('lead.errors.nameLength'),
            },
          });
        }
      }
    }

    if (name === 'email') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, email: t('lead.errors.required')}});
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
        setLead({...lead, errors: {...lead.errors, email: t('lead.errors.email')}});
      }
    }

    if (name === 'phoneNumber') {
      if (!value) {
        setLead({ ...lead, errors: { ...lead.errors, phoneNumber: t('lead.errors.required') } });
      } else if (!isPossiblePhoneNumber(value)) {
        setLead({ ...lead, errors: { ...lead.errors, phoneNumber: t('lead.errors.phoneNumber') } });
      }
    }

    if (name === 'procedure') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, procedure: t('lead.errors.required')}});
      }
    }

    if (name === 'language') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, language: t('lead.errors.required')}});
      }
    }

    if (name === 'messageChannel') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, messageChannel: t('lead.errors.messageChannel')}});
      }
    }

    if (name === 'messageChannelConsent') {
      if (lead.messageChannel && !value) {
        setLead({...lead, errors: {...lead.errors, messageChannelConsent: t('lead.errors.messageChannelConsent')}});
      }
    }
  };

  const validateStep2Fields = (name, value) => {
    if (leadForm.questions === 'Optional') return;

    if (name === 'height') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, height: t('lead.errors.required')}});
      }
    }

    if (name === 'weight') {
      if (!value) {
        setLead({...lead, errors: {...lead.errors, weight: t('lead.errors.required')}});
      }
    }
  };

  const getStep1Errors = (leadData) => {
    const errorUpdates = { ...leadData.errors };

    if (!leadData.fullName) {
      errorUpdates.fullName = t('lead.errors.required');
    } else if (leadData.fullName.split(' ').length < 2) {
      errorUpdates.fullName = t('lead.errors.lastName');
    } else if (!nameRegex.test(leadData.fullName)) {
      errorUpdates.fullName = t('lead.errors.nameValidation');
    } else {
      const nameParts = leadData.fullName.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      if (firstName.length < 1 || firstName.length > 50 || lastName.length < 1 || lastName.length > 50) {
        errorUpdates.fullName = t('lead.errors.nameLength');
      } else {
        delete errorUpdates.fullName;
      }
    }

    if (!leadData.email) {
      errorUpdates.email = t('lead.errors.required');
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(leadData.email)) {
      errorUpdates.email = t('lead.errors.email');
    } else {
      delete errorUpdates.email;
    }

    if (!leadData.phoneNumber) {
      errorUpdates.phoneNumber = t('lead.errors.required');
    } else if (!isPossiblePhoneNumber(leadData.phoneNumber)) {
      errorUpdates.phoneNumber = t('lead.errors.phoneNumber');
    } else {
      delete errorUpdates.phoneNumber;
    }

    if (!leadData.procedure) {
      errorUpdates.procedure = t('lead.errors.required');
    } else {
      delete errorUpdates.procedure;
    }

    if (!leadData.language) {
      errorUpdates.language = t('lead.errors.required');
    } else {
      delete errorUpdates.language;
    }

    if (!leadData.messageChannel) {
      errorUpdates.messageChannel = t('lead.errors.messageChannel');
      delete errorUpdates.messageChannelConsent;
    } else {
      delete errorUpdates.messageChannel;

      if (!leadData.messageChannelConsent) {
        errorUpdates.messageChannelConsent = t('lead.errors.messageChannelConsent');
      } else {
        delete errorUpdates.messageChannelConsent;
      }
    }

    return errorUpdates;
  };

  const STEP1_REQUIRED_FIELDS = [
    'fullName',
    'email',
    'phoneNumber',
    'procedure',
    'language',
    'messageChannel',
    'messageChannelConsent',
  ];

  const validateStep2 = (lead) => {
    if (leadForm.questions === 'Optional') {
      setStep2Valid(true);
      return (true);
    }

    if (!lead.passport) {
      setStep2Valid(false);
      return (false);
    }

    if (!lead.requireFinancing) {
      setStep2Valid(false);
      return (false);
    }

    if (leadForm.showCustomQuestion) {
      if (!lead.customQuestionAnswer) {
        setStep2Valid(false);
        return (false);
      }
    }

    if (leadForm.showWeightAndHeight) {
      if (useMetricSystem) {
        if (!lead.height) {
          setStep2Valid(false);
          return (false);
        }
      } else {
        if (!lead.height.includes(' ft') || !lead.height.includes(' in')) {
          setStep2Valid(false);
          return (false);
        }
      }

      if (!lead.weight) {
        setStep2Valid(false);
        return (false);
      }
    }

    if (!lead.howSoon) {
      setStep2Valid(false);
      return (false);
    }

    setStep2Valid(true);
    return (true);
  };

  useEffect(() => {
    if (id) {
      Axios.get(`${process.env.REACT_APP_API_URL}/api/v1/lead_forms/${id}?locale=${language}`).then(
        (response) => {
          const form = response.data.lead_form;
          setLeadForm(form);
          setLoaded(true);

          if (form.questions === 'Optional') setStep2Valid(true);
          const browserCode = (navigator.language || 'en').split('-')[0].toLowerCase();
          const targetName = LOCALE_TO_LANGUAGE_NAME[browserCode] || 'English';
          const languages = form.languages || [];
          const match =
            languages.find(
              (lang) =>
                lang.value?.toLowerCase() === targetName.toLowerCase() ||
                lang.label?.toLowerCase() === targetName.toLowerCase()
            ) ||
            languages.find(
              (lang) =>
                lang.value?.toLowerCase() === 'english' ||
                lang.label?.toLowerCase() === 'english'
            );
          if (match) {
            setLead((prev) => ({ ...prev, language: match.value }));
          }
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitStep1 = async (getRecaptchaToken) => {
    const errors = getStep1Errors(lead);
    if (STEP1_REQUIRED_FIELDS.some((field) => errors[field])) {
      setLead({ ...lead, errors });
      return;
    }

    const recaptchaToken = await getRecaptchaToken();
    const leadData = { ...lead, recaptchaToken };
    setSubmitting(true);

    const nextLead = {
      ...leadData,
      timeZone: timezone.name(),
      city: geoLocation.city,
      state: geoLocation.state,
      country: geoLocation.countryCode.toUpperCase(),
      url: window.location.href,
      leadFormId,
      channelId: leadForm.channelId,
      referringUrl: document.referrer,
    };

    Axios.post(`${process.env.REACT_APP_API_URL}/api/v1/leads`, { lead: nextLead }).then(
      (response) => {
        setSubmitting(false);
        setLead({ ...nextLead, uuid: response.data.lead.uuid });

        if (leadForm.questions === 'Hidden' && leadForm.redirectUrl) {
          window.location = leadForm.redirectUrl;
        } else {
          leadForm.questions === 'Hidden' ? setStep(3) : setStep(2);
        }
      }
    ).catch((err) => {
      setSubmitting(false);
      console.log('error', err.response?.data);
      if (err.response) {
        if (err.response.data.error === 'Phone number is invalid') {
          alert(t('invalidPhoneNumber'));
        } else {
          alert(err.response.data.error);
        }
      }
    });
  };

  const submitStep2 = () => {
    setSubmitting(true);
    if (leadForm.showCustomQuestion) lead.customQuestion = leadForm.customQuestion;
    if (useMetricSystem && lead.height) {
      lead.height = `${lead.height} cm`;
    }
    if (lead.weight) lead.weight = `${lead.weight} ${useMetricSystem ? 'kg' : 'lbs'}`;

    Axios.patch(`${process.env.REACT_APP_API_URL}/api/v1/leads/${lead.uuid}`, { lead: lead }).then(
      (response) => {
        if (leadForm.redirectUrl) {
          setSubmitting(false);
          window.location = leadForm.redirectUrl;
        } else {
          setSubmitting(false);
          setStep(3);
        }
      }
    ).catch((err) => {
      setSubmitting(false);
      if (err.response) alert(err.response.data.error);
    });
  }

  if (loaded) {
    if (step === 1)
      return (
        <Step1
          leadForm={leadForm}
          lead={lead}
          onChange={onChange}
          onBlur={onBlur}
          submitStep1={submitStep1}
          submitting={submitting}
          countryCode={geoLocation.countryCode}
          isMobile={isMobile}
          geoLocation={geoLocation}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
        />
      );
    else if (step === 2) {
      return (
        <Step2
          leadForm={leadForm}
          lead={lead}
          onChange={onChange}
          onBlur={onBlur}
          step2Valid={step2Valid}
          submitStep2={submitStep2}
          submitting={submitting}
          useMetricSystem={useMetricSystem}
          isMobile={isMobile}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
        />
      );
    } else if (step === 3) {
      return (
        <MultiStepLayout backgroundColor={leadForm.backgroundColor} foregroundColor={leadForm.titleColor} formBackgroundColor={leadForm.formBackgroundColor} isMobile={isMobile} showPoweredBy={true}>
          <Result successMessage={leadForm.successMessage} backgroundColor={leadForm.backgroundColor} isMobile={isMobile} />
        </MultiStepLayout>
      )
    }
  }
}

export default App;
